import {
  CoreApi,
  CornerSolutionComponent,
  Product,
  ProductType,
  SlidingDoorComponent
} from '@inter-ikea-storageone/range';
import {
  CornerAddonFrameComponent,
  doorOnCornerSolutionCanHaveHandles,
  isCornerFrameAllowingDoors
} from '@inter-ikea-storageone/range-pax';
import {
  ComponentIds,
  ConnectorsComponent,
  Entity,
  HierarchyUtils,
  Size
} from '@inter-ikea-core/core';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { getFilterOptions } from 'filters/SizeFilter/utils';
import { getAllowedHandleTypes } from 'rangeConfig/shared/utils/frontLayouts';
import { hideNoDoorsInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import { isMobile } from 'utils/platform';
import { getVisibleProductsForGroup } from 'ducks/products';
import { store } from 'ducks';

export const framesInSceneSupportSlidingDoors = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);
  const frameSizesInScene = framesInScene
    .map(
      frame =>
        frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
          ?.size
    )
    .filter((size): size is Size => !!size);
  const nuberOf50FramesInScene = frameSizesInScene.filter(size => size.width === 500).length;
  const nuberOf75FramesInScene = frameSizesInScene.filter(size => size.width === 750).length;
  const nuberOf100FramesInScene = frameSizesInScene.filter(size => size.width === 1000).length;
  return (
    framesInScene.length === 0 ||
    nuberOf100FramesInScene >= 2 ||
    nuberOf75FramesInScene >= 2 ||
    nuberOf50FramesInScene >= 4 ||
    (nuberOf100FramesInScene >= 1 && nuberOf50FramesInScene >= 2)
  );
};

export const checkIfChangeOrientationHidden = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) {
    return true;
  }
  return !coreApi.entities.isProductType(selectedEntity, ProductType.PAX_L_DIVIDER);
};

export const hideInnerLightsMenu = (coreApi: CoreApi, selectedEntityId: number) => {
  const selectedEntity = coreApi.entities.find(selectedEntityId);

  if (!selectedEntity) {
    return true;
  }

  const parent = HierarchyUtils.parent(selectedEntity);
  if (parent) {
    const parentSize = parent.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size;
    if (
      coreApi.entities.isProductType(selectedEntity, ProductType.PAX_SHELF) &&
      coreApi.entities.isProductType(parent, ProductType.PAX_ADD_ON_CORNER_FRAME) &&
      parentSize?.depth === 580
    ) {
      return true;
    }
  }

  const connectorsComponent = selectedEntity.getComponent<ConnectorsComponent>(
    ComponentIds.CONNECTORS_COMPONENT_ID
  );
  if (
    connectorsComponent &&
    connectorsComponent.connectors.find(connector => connector.key.startsWith('light'))
  ) {
    return false;
  }
  return true;
};

export const hideInnerLightOnCornerShelf = (coreApi: CoreApi, selectedEntityId: number) => {
  const selectedEntity = coreApi.entities.find(selectedEntityId);

  if (!selectedEntity) {
    return true;
  }
  const parent = HierarchyUtils.parent(selectedEntity);
  const connectorsComponent = selectedEntity.getComponent<ConnectorsComponent>(
    ComponentIds.CONNECTORS_COMPONENT_ID
  );
  if (parent && connectorsComponent) {
    const parentSize = parent.getComponent<CommunicationSizeComponent>(
      ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID
    )?.size;
    if (
      connectorsComponent.connectors.find(connector => connector.key.startsWith('light')) &&
      coreApi.entities.isProductType(selectedEntity, ProductType.PAX_SHELF) &&
      coreApi.entities.isProductType(parent, ProductType.PAX_ADD_ON_CORNER_FRAME) &&
      parentSize?.depth === 580
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Handles can only be added to one of the doors of a corner solution.
 * This should be on the door on the regular frame except if that door
 * is 25cm wide, then it should be on the door on the corner frame.
 */
export const hideHandlesMenuOnDoorOnCornerFrame = (coreApi: CoreApi) => {
  const selectedEntity = coreApi.entities.findSelectedEntity();
  if (!selectedEntity) return false;
  return !doorOnCornerSolutionCanHaveHandles(selectedEntity, coreApi);
};

export const hideOpeningWidget = (coreApi: CoreApi) => {
  const selectedDoor = coreApi.entities.findSelectedEntity();
  const frame = selectedDoor && coreApi.entities.getParent(selectedDoor);

  if (!frame) return false;

  const isCornerSolution = !!HierarchyUtils.getAncestors(frame).find(e =>
    e.hasComponent(CornerSolutionComponent.id)
  );

  return (
    frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
      ?.size.width !== 500 || isCornerSolution
  );
};

export const hideHeightToggle = (coreApi: CoreApi, products: Product[]) => {
  const frameProduct = products.filter(
    (product: { type: ProductType }) => product.type === ProductType.PAX_FRAME
  );
  const availableHeights = getFilterOptions('height')(frameProduct);

  return Object.keys(availableHeights).length < 2;
};

export const hideDepthToggle = (coreApi: CoreApi, products: Product[]) => {
  const frameProduct = products.filter(
    (product: { type: ProductType }) => product.type === ProductType.PAX_FRAME
  );
  const availableDepths = getFilterOptions('depth')(frameProduct);

  return Object.keys(availableDepths).length < 2;
};

// Error to be displayed when user tries to add handles to a corner solution, but handle is only showing on one of the doors
export const hidePaxDoorHandlesOnCornerSolutionErrorMessage = (
  coreApi: CoreApi,
  selectedEntity?: Entity
) => {
  if (selectedEntity) {
    return (
      coreApi.entities.getAncestorsWithComponent(selectedEntity, CornerAddonFrameComponent.id)
        .length === 0 || !hideNoDoorsInSceneErrorMessage(coreApi)
    );
  } else {
    return (
      coreApi.entities.getByComponent(CornerSolutionComponent.id).length === 0 ||
      !hideNoDoorsInSceneErrorMessage(coreApi)
    );
  }
};

// Error to be displayed when user tries to add doors to corner solution that is 100cm wide and 35cm deep
export const hidePaxCornerFrameOpenCombinationErrorMessage = (coreApi: CoreApi) => {
  const framesInScene = coreApi.entities
    .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
    .filter(c => !c.getComponent<CornerSolutionComponent>(CornerSolutionComponent.id));

  const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);
  const openCornerFrameInScene = cornerFramesInScene.filter(
    c => !isCornerFrameAllowingDoors(coreApi, c)
  );

  const regularFramesInScene = framesInScene.length > 2 * openCornerFrameInScene.length;
  return openCornerFrameInScene.length === 0 || regularFramesInScene;
};

// Toast specific case of the above scenario (Same rules on mobile, but on desktop we sometime use widget and sometimes toast)
export const hidePaxCornerFrameOpenCombinationErrorMessageToast = (coreApi: CoreApi) => {
  const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);
  const openCornerFrameInScene = cornerFramesInScene.filter(
    c => !isCornerFrameAllowingDoors(coreApi, c)
  );

  // Displayed as widget in the following scenario, so toast must be hidden
  const alreadyDisplayed = !hidePaxCornerFrameOpenCombinationErrorMessage(coreApi) && !isMobile();

  return openCornerFrameInScene.length === 0 || alreadyDisplayed;
};

// Error to be displayed when
export const hidePaxCornerWithoutDoorsErrorMessage = (coreApi: CoreApi) => {
  const cornerFramesInScene = coreApi.entities.getByComponent(CornerSolutionComponent.id);
  const openCornerFrameInScene = cornerFramesInScene.find(
    c => !isCornerFrameAllowingDoors(coreApi, c)
  );
  return !openCornerFrameInScene;
};

// Error to be displayed when user tries to add handles to a solution with sliding doors
export const hidePaxHandlesNotApplicableOnSlidingDoors = (
  coreApi: CoreApi,
  selectedEntity?: Entity
) => {
  const doorsInScene = selectedEntity
    ? [
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, ComponentIds.DOOR_COMPONENT_ID),
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, SlidingDoorComponent.id)
      ].flat()
    : coreApi.entities.getByComponents([ComponentIds.DOOR_COMPONENT_ID, SlidingDoorComponent.id]);

  const frontsWithIntegratedHandles = doorsInScene.filter(
    front => getAllowedHandleTypes(front).length === 0
  );

  const slidingDoors = doorsInScene.filter(door =>
    coreApi.entities.isOneOfProductTypes(door, [
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ])
  );

  const slidingDoorsWithIntegratedHandles =
    slidingDoors.length === frontsWithIntegratedHandles.length &&
    frontsWithIntegratedHandles.length > 0;

  return !slidingDoorsWithIntegratedHandles;
};

// Error to be displayed when user tries to add handles to a solution with integrated handles
export const hidePaxHandlesNotApplicableOnDoorsWithIntegratedHandles = (
  coreApi: CoreApi,
  selectedEntity?: Entity
) => {
  const doorsInScene = selectedEntity
    ? [
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, ComponentIds.DOOR_COMPONENT_ID),
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, SlidingDoorComponent.id)
      ].flat()
    : coreApi.entities.getByComponents([ComponentIds.DOOR_COMPONENT_ID, SlidingDoorComponent.id]);

  const frontsWithIntegratedHandles = doorsInScene.filter(
    front => getAllowedHandleTypes(front).length === 0
  );

  const slidingDoors = doorsInScene.filter(door =>
    coreApi.entities.isOneOfProductTypes(door, [
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ])
  );

  const nonSlidingIntegratedHandles =
    slidingDoors.length === 0 &&
    frontsWithIntegratedHandles.length > 0 &&
    doorsInScene.length === frontsWithIntegratedHandles.length;

  return !nonSlidingIntegratedHandles;
};

// Error to be displayed when user tries to change handles on a solution with mixed doors
export const hidePaxDoorHandlesOnDoorsMixedIncludedIntegratedHandles = (
  coreApi: CoreApi,
  selectedEntity?: Entity
) => {
  const doorsInScene = selectedEntity
    ? [
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, ComponentIds.DOOR_COMPONENT_ID),
        HierarchyUtils.getDescendantsWithComponent(selectedEntity, SlidingDoorComponent.id)
      ].flat()
    : coreApi.entities.getByComponents([ComponentIds.DOOR_COMPONENT_ID, SlidingDoorComponent.id]);

  const frontsWithIntegratedHandles = doorsInScene.filter(
    front => getAllowedHandleTypes(front).length === 0
  );

  const slidingDoors = doorsInScene.filter(door =>
    coreApi.entities.isOneOfProductTypes(door, [
      ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
      ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
    ])
  );

  const mixedIncludedAndSlidingWithIntegratedHandles =
    slidingDoors.length === 0 &&
    frontsWithIntegratedHandles.length > 0 &&
    doorsInScene.length !== frontsWithIntegratedHandles.length;

  return !mixedIncludedAndSlidingWithIntegratedHandles;
};

export const hideNoFramesForSelection = () => {
  const products = getVisibleProductsForGroup(store.getState());
  return products.length > 0;
};
